import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Dropdown,
} from "react-bootstrap";
import { AuthContext } from "../../../context/AuthContext";
import { userService } from "../../../services/api";
import "../../../styles/UserProfile.css";

const UserProfile = () => {
  const { currentUser, logout } = useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [profileData, setProfileData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    role: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  useEffect(() => {
    if (currentUser) {
      const isAdmin = currentUser.profile.role === "admin";
      setIsAdmin(isAdmin);
      setProfileData({
        username: currentUser.username || "",
        email: currentUser.email || "",
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        role: currentUser.profile?.role || "",
      });
      setFormData({
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        email: currentUser.email || "",
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await userService.patchme(formData);
      setProfileData({
        ...profileData,
        ...response.data,
      });
      setSuccess(true);
      setEditMode(false);
    } catch (err) {
      setError("Une erreur est survenue lors de la mise à jour du profil.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleEditMode = () => {
    if (editMode) {
      setFormData({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
      });
    }
    setEditMode(!editMode);
    setSuccess(false);
    setError(null);
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/connexion";
  };

  if (!currentUser) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        paddingTop: '80px' // Ajout de padding-top pour éviter le header
      }}>
        <div style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '15px', padding: '20px', color: '#856404' }}>
          Vous devez être connecté pour accéder à votre profil.
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      paddingTop: '80px' // Ajout de padding-top pour éviter le header fixe
    }}>
      {/* Sidebar fixe */}
      <div style={{
        width: '320px',
        background: 'linear-gradient(180deg,rgb(114, 160, 206) 0%,rgb(234, 239, 244) 100%)',
        boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
        position: 'fixed',
        height: 'calc(100vh - 80px)', // Ajustement de la hauteur pour tenir compte du header
        left: 0,
        top: '80px', // Positionnement sous le header
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header de la sidebar avec avatar */}
        
        <div style={{
          padding: '20px 30px',
          textAlign: 'center',
          borderBottom: '1px solid rgba(58, 162, 242, 0.1)',
          background: 'linear-gradient(135deg, rgba(225, 236, 241, 0.1) 0%, rgba(233, 231, 231, 0.05) 100%)'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            margin: '0 auto 20px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            color: 'white',
            fontWeight: 'bold',
            boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {currentUser.profile_image ? (
              <img
                src={currentUser.profile_image}
                alt="Photo de profil"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            ) : (
              <span>{`${profileData.first_name[0] || ''}${profileData.last_name[0] || ''}`}</span>
            )}
          </div>
          <h4 style={{ color: 'white', margin: '0 0 8px 0', fontWeight: '600' }}>
            {`${profileData.first_name} ${profileData.last_name}`}
          </h4>
          <p style={{ color: 'rgba(11, 11, 11, 0.7)', margin: '0 0 15px 0', fontSize: '14px' }}>
            {profileData.email}
          </p>
          {isAdmin && (
            <span style={{
              background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 15px rgba(231,76,60,0.3)'
            }}>
              Admin
            </span>
          )}
        </div>

        {/* Menu de navigation */}
        <div style={{ flex: 1, padding: '20px 0' }}>
          <div>
            {[
              { icon: '👤', label: 'Mon Profil', active: true },
              { icon: '✏️', label: 'Mes Contributions', href: '/MesContrib.js' },
              { icon: '🕒', label: 'Historique' },
              { icon: '⚙️', label: 'Paramètres' }
            ].map((item, index) => (
              <a
                key={index}
                href="#"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '15px 30px',
                  color: item.active ? 'white' : 'rgba(11, 7, 7, 0.7)',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  background: item.active ? 'rgba(255,255,255,0.1)' : 'transparent',
                  borderRight: item.active ? '4px solid #667eea' : '4px solid transparent',
                  fontSize: '15px',
                  fontWeight: item.active ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (!item.active) {
                    e.target.style.background = 'rgba(255,255,255,0.05)';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.active) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'rgba(255,255,255,0.7)';
                  }
                }}
              >
                <span style={{ marginRight: '15px', fontSize: '18px' }}>{item.icon}</span>
                {item.label}
              </a>
            ))}
          </div>
        </div>

        {/* Bouton de déconnexion */}
        <div style={{ padding: '20px 30px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              alignItems: 'center',
               
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(231,76,60,0.3)'
              
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 25px rgba(231,76,60,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(231,76,60,0.3)';
            }}
          >
             Se déconnecter
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      <div style={{ 
        marginLeft: '320px', // Changé de 300px à 320px pour correspondre à la largeur de la sidebar
        flex: 1, 
        padding: '20px',
        minHeight: 'calc(100vh - 80px)' // Ajustement de la hauteur pour tenir compte du header
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          {/* Header du contenu principal */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '30px 40px',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontWeight: '700', fontSize: '28px' }}>
                Profil Personnel
              </h2>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '16px' }}>
                Gérez vos informations personnelles
              </p>
            </div>
            <button
              onClick={toggleEditMode}
              style={{
                background: editMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = editMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {editMode ? '❌ Annuler' : '✏️ Modifier'}
            </button>
          </div>

          {/* Corps du contenu */}
          <div style={{ padding: '40px' }}>
            {success && (
              <div style={{
                background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                color: 'white',
                padding: '15px 20px',
                borderRadius: '12px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 4px 15px rgba(46,204,113,0.3)'
              }}>
                <span style={{ marginRight: '12px', fontSize: '20px' }}>✅</span>
                Profil mis à jour avec succès !
              </div>
            )}
            
            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: 'white',
                padding: '15px 20px',
                borderRadius: '12px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 4px 15px rgba(231,76,60,0.3)'
              }}>
                <span style={{ marginRight: '12px', fontSize: '20px' }}>❌</span>
                {error}
              </div>
            )}

            {editMode ? (
              <Form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
                  <Form.Group>
                    <Form.Label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Prénom
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '15px 20px',
                        border: '2px solid #e1e8ed',
                        borderRadius: '12px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: '#f8f9fa'
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Nom
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '15px 20px',
                        border: '2px solid #e1e8ed',
                        borderRadius: '12px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        background: '#f8f9fa'
                      }}
                    />
                  </Form.Group>
                </div>
                
                <Form.Group style={{ marginBottom: '40px' }}>
                  <Form.Label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '15px 20px',
                      border: '2px solid #e1e8ed',
                      borderRadius: '12px',
                      fontSize: '16px',
                      transition: 'all 0.3s ease',
                      background: '#f8f9fa'
                    }}
                  />
                </Form.Group>

                <div style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    onClick={toggleEditMode}
                    disabled={loading}
                    style={{
                      background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '15px 30px',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading 
                        ? 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '15px 30px',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      minWidth: '140px'
                    }}
                  >
                    {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
                  </Button>
                </div>
              </Form>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    padding: '25px',
                    borderRadius: '15px',
                    border: '1px solid #e1e8ed'
                  }}>
                    <h6 style={{
                      color: '#667eea',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      fontWeight: '700',
                      letterSpacing: '1px',
                      marginBottom: '12px'
                    }}>
                      Prénom
                    </h6>
                    <p style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      margin: 0
                    }}>
                      {profileData.first_name || 'Non renseigné'}
                    </p>
                  </div>
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    padding: '25px',
                    borderRadius: '15px',
                    border: '1px solid #e1e8ed'
                  }}>
                    <h6 style={{
                      color: '#667eea',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      fontWeight: '700',
                      letterSpacing: '1px',
                      marginBottom: '12px'
                    }}>
                      Nom
                    </h6>
                    <p style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      margin: 0
                    }}>
                      {profileData.last_name || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    padding: '25px',
                    borderRadius: '15px',
                    border: '1px solid #e1e8ed'
                  }}>
                    <h6 style={{
                      color: '#667eea',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      fontWeight: '700',
                      letterSpacing: '1px',
                      marginBottom: '12px'
                    }}>
                      Email
                    </h6>
                    <p style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      margin: 0
                    }}>
                      {profileData.email || 'Non renseigné'}
                    </p>
                  </div>
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    padding: '25px',
                    borderRadius: '15px',
                    border: '1px solid #e1e8ed'
                  }}>
                    <h6 style={{
                      color: '#667eea',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      fontWeight: '700',
                      letterSpacing: '1px',
                      marginBottom: '12px'
                    }}>
                      Rôle
                    </h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <p style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        margin: 0,
                        textTransform: 'capitalize'
                      }}>
                        {profileData.role || 'Non défini'}
                      </p>
                      {isAdmin && (
                        <span style={{
                          background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '15px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;